import React, { useState } from 'react';
import {
  Box,
  Button,
  Divider,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Switch,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { CloudUpload, Delete } from '@mui/icons-material';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { uk } from 'date-fns/locale';
import { useTranslation } from 'react-i18next';

import { LocalizedInput } from '@components/common/LocalizedInput';
import { BaseDialog } from '@components/common/BaseDialog';
import { ModalIndices } from '../../common/i-doc/i-doc.registry.ts';
import { ExpertPostVisibility } from '@/types/expert-posts';
import { Locale } from '@/services/workout.service';
import { useExpertPostOptions } from '@/hooks/useExpertPostOptions';

export interface PostFormData {
  titleI18n: Partial<Record<Locale, string>>;
  contentI18n: Partial<Record<Locale, string>>;
  slug: string;
  isPublished: boolean;
  publishedAt: Date | null;
  coverImageId?: number | null;
  visibility: ExpertPostVisibility | null;
}

export const initialPostFormData: PostFormData = {
  titleI18n: {},
  contentI18n: {},
  slug: '',
  isPublished: true,
  publishedAt: new Date(),
  visibility: null,
};

interface ExpertPostFormDialogProps {
  open: boolean;
  isEditing: boolean;
  formData: PostFormData;
  submitting: boolean;
  previewUrl: string | null;
  onClose: () => void;
  onSubmit: (e: React.FormEvent) => void;
  onChange: (data: PostFormData) => void;
  onFileChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onRemoveCover: () => void;
}

export const ExpertPostFormDialog: React.FC<ExpertPostFormDialogProps> = ({
  open,
  isEditing,
  formData,
  submitting,
  previewUrl,
  onClose,
  onSubmit,
  onChange,
  onFileChange,
  onRemoveCover,
}) => {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('md'));
  const { t } = useTranslation();
  const { visibilities } = useExpertPostOptions();
  // FIX: той самий баг, що й в AdminNewsPage — заголовок і текст мали кожен
  // власну незалежну вкладку мови (LocalizedInput без activeTab-пропу),
  // перемикання для одного поля не відображалось на іншому. Один спільний стан.
  const [activeLocaleTab, setActiveLocaleTab] = useState(0);

  const getVisibilityLabel = (value: string, fallback: string) =>
    visibilities.find((o) => o.value === value)?.label ?? fallback;

  return (
    <BaseDialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullScreen={fullScreen}
      iDoc={ModalIndices.EXPERT_POST_FORM}
      title={isEditing ? t('expertPosts.form.titleEdit') : t('expertPosts.form.titleCreate')}
      actions={
        <Button
          type="submit"
          form="post-form"
          variant="contained"
          disabled={submitting}
          fullWidth={fullScreen}
          size={fullScreen ? 'large' : 'medium'}
        >
          {submitting ? t('expertPosts.form.saving') : t('expertPosts.form.save')}
        </Button>
      }
    >
      <form id="post-form" onSubmit={onSubmit}>
        <Grid container spacing={fullScreen ? 2 : 3} sx={{ mt: 0 }}>
          {/* Main content: title and text */}
          <Grid item xs={12} md={8}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: fullScreen ? 2 : 3 }}>
              <LocalizedInput
                label={t('expertPosts.form.titleField')}
                value={formData.titleI18n}
                onChange={(v) => onChange({ ...formData, titleI18n: v })}
                activeTab={activeLocaleTab}
                onTabChange={setActiveLocaleTab}
              />
              <LocalizedInput
                label={t('expertPosts.form.contentField')}
                multiline
                rows={fullScreen ? 8 : 12}
                value={formData.contentI18n}
                onChange={(v) => onChange({ ...formData, contentI18n: v })}
                activeTab={activeLocaleTab}
                onTabChange={setActiveLocaleTab}
              />
            </Box>
          </Grid>

          {/* Side panel: settings and image */}
          <Grid item xs={12} md={4}>
            <Paper variant="outlined" sx={{ p: 2, height: '100%', bgcolor: fullScreen ? 'background.default' : 'background.paper' }}>
              <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                {t('expertPosts.form.settings')}
              </Typography>
              <Stack spacing={2.5}>
                <FormControl fullWidth size={fullScreen ? 'small' : 'medium'}>
                  <InputLabel id="post-visibility-label">{t('expertPosts.form.visibility')}</InputLabel>
                  <Select
                    labelId="post-visibility-label"
                    label={t('expertPosts.form.visibility')}
                    value={formData.visibility ?? 'all'}
                    onChange={(e) => {
                      const val = e.target.value;
                      onChange({ ...formData, visibility: val === 'all' ? null : (val as ExpertPostVisibility) });
                    }}
                  >
                    <MenuItem value="all">{t('expertPosts.form.visibilityAll')}</MenuItem>
                    <MenuItem value="clients">{getVisibilityLabel('clients', t('expertPosts.form.visibilityClients'))}</MenuItem>
                    <MenuItem value="website">{getVisibilityLabel('website', t('expertPosts.form.visibilityWebsite'))}</MenuItem>
                  </Select>
                </FormControl>

                <TextField
                  fullWidth
                  size={fullScreen ? 'small' : 'medium'}
                  label={t('expertPosts.form.slug')}
                  helperText={t('expertPosts.form.slugHelper')}
                  value={formData.slug}
                  onChange={(e) => onChange({ ...formData, slug: e.target.value })}
                />

                <FormControlLabel
                  control={
                    <Switch
                      size={fullScreen ? 'small' : 'medium'}
                      checked={formData.isPublished}
                      onChange={(e) => {
                        const checked = e.target.checked;
                        onChange({
                          ...formData,
                          isPublished: checked,
                          publishedAt: checked && !formData.publishedAt ? new Date() : formData.publishedAt,
                        });
                      }}
                    />
                  }
                  label={t('expertPosts.form.publish')}
                />

                <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={uk}>
                  <DateTimePicker
                    label={t('expertPosts.form.publishedAt')}
                    value={formData.publishedAt}
                    onChange={(v) => onChange({ ...formData, publishedAt: v })}
                    slotProps={{ textField: { fullWidth: true, size: fullScreen ? 'small' : 'medium' } }}
                  />
                </LocalizationProvider>

                <Divider />

                <Box>
                  <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                    {t('expertPosts.form.cover')}
                  </Typography>
                  <input
                    accept="image/*"
                    style={{ display: 'none' }}
                    id="post-cover-input"
                    type="file"
                    onChange={onFileChange}
                  />
                  <label htmlFor="post-cover-input">
                    <Button
                      variant="outlined"
                      component="span"
                      fullWidth
                      startIcon={<CloudUpload />}
                      size={fullScreen ? 'small' : 'medium'}
                    >
                      {t('expertPosts.form.upload')}
                    </Button>
                  </label>
                  {previewUrl && (
                    <Box
                      mt={2}
                      position="relative"
                      sx={{
                        border: '1px dashed #ccc',
                        borderRadius: 1,
                        p: 1,
                        display: 'flex',
                        justifyContent: 'center',
                        bgcolor: 'background.paper',
                        maxHeight: fullScreen ? 150 : 200,
                        overflow: 'hidden'
                      }}
                    >
                      <img
                        src={previewUrl}
                        alt="Preview"
                        style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
                      />
                      <IconButton
                        size="small"
                        onClick={onRemoveCover}
                        aria-label={t('expertPosts.form.removeCover')}
                        sx={{
                          position: 'absolute',
                          right: 4,
                          top: 4,
                          bgcolor: 'rgba(0,0,0,0.5)',
                          color: 'white',
                          '&:hover': { bgcolor: 'rgba(0,0,0,0.7)' },
                        }}
                      >
                        <Delete fontSize="small" />
                      </IconButton>
                    </Box>
                  )}
                </Box>
              </Stack>
            </Paper>
          </Grid>
        </Grid>
      </form>
    </BaseDialog>
  );
};
